import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/Alert';
import { useFitbitStatus } from '../hooks/useFitbitStatus';
import ActivityChart from '../components/fitbit/ActivityChart';
import SleepChart from '../components/fitbit/SleepChart';
import HeartRateChart from '../components/fitbit/HeartRateChart';
import WeightChart from '../components/fitbit/WeightChart';
import SpO2Chart from '../components/fitbit/SpO2Chart';
import TemperatureChart from '../components/fitbit/TemperatureChart';
import NutritionDataView from '../components/data/NutritionDataView';
import HydrationDataView from '../components/data/HydrationDataView';
import MitoIndexCard from '../components/twin/MitoIndexCard';
import TodayTimeline from '../components/home/TodayTimeline';

const TABS = [
  { key: 'activity', label: 'Activity', fitbit: true },
  { key: 'sleep', label: 'Sleep', fitbit: true },
  { key: 'heart-rate', label: 'Heart Rate', fitbit: true },
  { key: 'weight', label: 'Weight', fitbit: true },
  { key: 'spo2', label: 'SpO2', fitbit: true },
  { key: 'temperature', label: 'Temperature', fitbit: true },
  { key: 'nutrition', label: 'Nutrition', fitbit: false },
  { key: 'hydration', label: 'Hydration', fitbit: false },
  { key: 'entries', label: 'Entries', fitbit: false },
];

const DataPage = () => {
  const { fitbitStatus, loading: loadingStatus } = useFitbitStatus();
  const [activeTab, setActiveTab] = useState('activity');

  const currentTab = TABS.find((t) => t.key === activeTab);
  const isFitbitTab = currentTab?.fitbit;
  const showFitbitWarning = isFitbitTab && !loadingStatus && !fitbitStatus.connected;

  const renderTabContent = () => {
    if (showFitbitWarning) {
      return (
        <Alert variant="default">
          <AlertDescription>
            Your Fitbit account is not connected. Connect it in your{' '}
            <Link to="/me" className="text-primary-600 hover:underline">
              profile settings
            </Link>{' '}
            to view {currentTab.label.toLowerCase()} data.
          </AlertDescription>
        </Alert>
      );
    }

    switch (activeTab) {
      case 'activity':
        return <ActivityChart />;
      case 'sleep':
        return <SleepChart />;
      case 'heart-rate':
        return <HeartRateChart />;
      case 'weight':
        return <WeightChart />;
      case 'spo2':
        return <SpO2Chart />;
      case 'temperature':
        return <TemperatureChart />;
      case 'nutrition':
        return <NutritionDataView />;
      case 'hydration':
        return <HydrationDataView />;
      case 'entries':
        return <TodayTimeline />;
      default:
        return <ActivityChart />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
          Data
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Review your health data and trends
        </p>
      </div>

      {/* Featured composite readout — a rolled-up view of the raw biometrics
          in the tabs below. A derived index, not raw data, so it leads. */}
      <div className="mb-8">
        <MitoIndexCard />
      </div>

      {/* Tab Bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Loading state for Fitbit status check */}
      {loadingStatus && isFitbitTab ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        renderTabContent()
      )}
    </div>
  );
};

export default DataPage;
